import React from 'react';
import { Skeleton, Tooltip, Typography } from '@mui/material';
import {
	DEAL_STATUS_TYPES,
	formatCurrency,
	formatDate,
	formatKey,
	getOutstandingDays,
	getUpperCaseHeader,
	getUrlFromPipeline,
	safelyParseFloat,
} from '@/utils/constants';
import { paths } from '@/app/routes';
import { OwnerCellData } from '../OwnerCell';
import DataGridBrokerGroup from '@/components/table/DataGridComponents/DataGridBrokerGroup';
import DataGridFormattedDate from '@/components/table/DataGridComponents/DataGridFormattedDate';
import DataGridClientName from '@/components/table/DataGridComponents/DataGridClientName';
import DataGridContactPhone from '@/components/table/DataGridComponents/DataGridContactPhone';
import DataGridContactName from '@/components/table/DataGridComponents/DataGridContactName';
import DataGridContactEmail from '@/components/table/DataGridComponents/DataGridContactEmail';
import { safelyParseDate } from '@/utils/date';
import { getPolicyTransactionInvoiceLabel } from '@/utils/policyTransactionLabel';
import DataGridDealStage from '@/components/table/DataGridComponents/DataGridDealStage';
import DataGridInformedConsent from '@/components/table/DataGridComponents/DataGridInformedConsent';
import { CONSENT_STATUS_OPTIONS, getRowConsentStatus } from '@/features/informedConsent/helpers';
import DataGridProspectStage from '@/components/table/DataGridComponents/DataGridProspectStage';
import DataGridDealPolicies from '@/components/table/DataGridComponents/DataGridDealPolicies';
import DataGridOutstandingDays from '@/components/table/DataGridComponents/DataGridOutstandingDays';
import DataGridDealName from '@/components/table/DataGridComponents/DataGridDealName';
import { PriorityChip } from '@/components/PriorityChip';
import { DataGridTaskType } from '@/components/table/DataGridComponents/DataGridTaskType';
import { DataGridTaskStatus } from '@/components/table/DataGridComponents/DataGridTaskStatus';
import { DataGridTaskCategory } from '@/components/table/DataGridComponents/DataGridTaskCategory';
import { DataGridTaskObjectType } from '@/components/table/DataGridComponents/DataGridTaskObjectType';
import { DataGridTaskName } from '@/components/table/DataGridComponents/DataGridTaskName';
import { DataGridTaskClientName } from '@/components/table/DataGridComponents/DataGridTaskClientName';
import { DataGridTaskContactName } from '@/components/table/DataGridComponents/DataGridTaskContactName';
import DataGridReferralStatus from '@/components/table/DataGridComponents/DataGridReferralStatus';
import DataGridPipeline from '@/components/table/DataGridComponents/DataGridPipeline';
import RenderRenewalStatusCell from '@/components/tables/PolicyTransactionTable/PolicyTransactionDataGrid/RenderRenewalStatusCell';
import { getTransactions } from '@/components/tables/PolicyTransactionTable/PolicyTransactionDataGrid/helper';
import DataGridDealActions from '@/components/table/DataGridComponents/DataGridDealActions';
import DebtorAutomationToggle from '@/components/DebtorAutomationToggle';
import {
	RELATIONSHIP_CONTACT_STATUS_LABELS,
	RELATIONSHIP_CONTACT_TYPE_LABELS,
} from '@/utils/relationshipContactOptions';

export const parseHeadCells = (headCells) =>
	headCells.map((c) => ({
		field: c.id,
		headerName: c.label,
		flex: 1,
		sortable: c.sortable ?? false,
		...(c.sortingOrder && { sortingOrder: c.sortingOrder }),
		...(c.reorderable === false && { reorderable: false }),
		display: 'flex',
		...((c.id === 'name' ||
			c.id === 'dealName' ||
			c.id === 'fullName' ||
			c.hideable === false) && {
			hideable: false,
			disableColumnMenu: true,
		}),
	}));

export const mapColumns = ({ column, objectType }) => {
	const field = column.field;

	column.minWidth = 100;
	column.display = 'flex';

	if (
		['dealName', 'clientName', 'name', 'fullName', 'policies', 'invoices', 'subject'].includes(
			field,
		)
	) {
		column.flex = 2;
		column.minWidth = 150;
	}
	if (objectType === 'tasks' && !['status', 'taskType', 'priority'].includes(column.field)) {
		column.minWidth = 150;
	}

	// column.renderHeader = (params) => (
	// 	<Tooltip title={params?.colDef?.headerName} placement='top' disableInteractive>
	// 		<Typography variant='inherit'>{params?.colDef?.headerName}</Typography>
	// 	</Tooltip>
	// );

	switch (field) {
		case 'ownerDocId':
		case 'clientOwnerDocId':
		case 'ownerId':
		case 'clientOwnerId':
		case 'createdBy':
		case 'lastUpdatedBy':
			column.renderCell = ({ row }) => {
				const ownerDocId = getOwnerDocId(row, field);
				if (!ownerDocId) return <></>;
				return <OwnerCellData userId={ownerDocId} color={'text.primary'} />;
			};
			break;
		case 'brokerGroupId':
			column.renderCell = ({ row }) => {
				return <DataGridBrokerGroup {...{ row }} />;
			};
			break;
		case 'lifeInsuranceReferralStatus':
		case 'kiwiSaverReferralStatus':
			column.renderCell = ({ row }) => {
				return <DataGridReferralStatus fieldName={field} row={row} />;
			};
			break;
		case 'prospectAmount':
		case 'dealAmount':
			column.renderCell = ({ row }) => {
				const num = row[field] ?? 0;
				return (
					<Typography>
						{formatCurrency({
							num,
							showDecimal: true,
						})}
					</Typography>
				);
			};
			break;
		case 'createdDate':
		case 'createDate':
		case 'createdWhen':
		case 'dateCreated':
		case 'estimatedCloseDate':
		case 'renewalDate':
		case 'dateOfLoss':
		case 'dateClosed':
		case 'lastUpdated':
		case 'createdAt':
			column.renderCell = ({ row }) => {
				const date = row[field];
				return (
					<DataGridFormattedDate
						date={date}
						includeTime={field === 'lastUpdated' || field === 'createdAt'}
					/>
				);
			};
			break;
		case 'nextTaskDate':
			column.renderCell = ({ row }) => {
				const date = row[field];
				return <DataGridFormattedDate date={date} showOverdue={true} includeTime={true} />;
			};
			break;
		case 'dueDate':
			column.renderCell = ({ row }) => {
				return (
					<DataGridFormattedDate
						date={row.dueDate}
						showOverdue={!row.completed}
						includeTime={true}
					/>
				);
			};
			break;
		case 'effectiveDate':
			column.renderCell = ({ row }) => {
				return <DataGridOutstandingDays {...row} />;
			};
			break;
		case 'clientName':
		case 'name':
		case 'deal':
			column.renderCell = ({ row }) => {
				if (row.indexName === 'tasks') {
					return <DataGridTaskClientName row={row} />;
				}
				return <DataGridClientName {...{ row, objectType }} />;
			};
			break;
		case 'contactPhone':
			column.renderCell = ({ row }) => {
				return <DataGridContactPhone phone={row.contactPhone} />;
			};
			break;
		case 'phone':
			column.renderCell = ({ row }) => {
				return <DataGridContactPhone phone={row.phone} />;
			};
			break;
		case 'contactName':
			column.renderCell = ({ row }) => {
				if (row.indexName === 'tasks') {
					return <DataGridTaskContactName row={row} />;
				}
				return <DataGridContactName {...{ row, objectType }} />;
			};
			break;
		case 'contactEmail':
			column.renderCell = ({ row }) => {
				return <DataGridContactEmail email={row.contactEmail} />;
			};
			break;
		case 'email':
			column.renderCell = ({ row }) => {
				return <DataGridContactEmail email={row.email} />;
			};
			break;
		case 'referralPartner':
			column.renderCell = ({ row }) => {
				return <DataGridContactEmail email={row.referralPartner} />;
			};
			break;
		case 'isProspect':
			column.renderCell = ({ row }) => {
				return <Typography>{row.isProspect ? 'Prospect' : 'Client'}</Typography>;
			};
			break;
		case 'dealName':
			column.renderCell = ({ row }) => {
				return <DataGridDealName {...{ row }} />;
			};
			break;
		case 'automationEnabled':
			column.renderCell = ({ row }) => {
				return (
					<DebtorAutomationToggle
						dealId={row.dealId}
						automationEnabled={row.automationEnabled}
						disabled={Boolean(row.policiesLoading)}
					/>
				);
			};
			break;
		case 'dealStage':
			column.renderCell = ({ row }) => {
				return <DataGridDealStage {...row} />;
			};
			break;
		case 'informedConsentStatus':
			column.minWidth = 140;
			column.renderCell = ({ row }) => {
				return <DataGridInformedConsent {...row} />;
			};
			break;
		case 'dealStatus':
			column.renderCell = ({ row }) => {
				const { transactions } = getTransactions(row.policies);

				Object.entries(transactions).forEach(([policyId, trans]) => {
					const policy = trans?.policy?.policy;
					// Grab latest transaction otherwise first
					const transaction =
						trans?.transactions?.find((t) => t?.IsLatestInPeriod) ??
						trans?.transactions?.[0];
					row.policy = policy;
					row.transaction = transaction;
				});
				return <RenderRenewalStatusCell {...{ row }} />;
			};
			break;
		case 'actions': {
			if (!column.renderCell) {
				column.renderCell = ({ row }) => <DataGridDealActions {...{ row }} />;
			}
			column.minWidth = column.minWidth ?? 50;
			column.maxWidth = column.maxWidth ?? 50;
			column.width = column.width ?? 50;
			break;
		}
		case 'pipeline':
			column.renderCell = ({ row }) => <DataGridPipeline {...{ pipeline: row.pipeline }} />;
			break;
		case 'stage':
			column.renderCell = ({ row }) => {
				return <DataGridProspectStage stage={row.stage} />;
			};
			break;
		case 'policies':
			column.sortable = false;
			column.renderCell = ({ row }) => {
				const policies = row.policies?.map((p) => p?.policy);
				return (
					<DataGridDealPolicies
						{...{
							...row,
							policies,
							policiesLoading: row.policiesLoading,
						}}
					/>
				);
			};
			break;
		case 'invoices':
			column.sortable = false;
			column.renderCell = ({ row }) => {
				const transactions = (row?.policies ?? [])
					.map((p) => p?.transaction)
					.filter(Boolean)
					.reduce((acc, trans) => {
						if (!trans?.Id) return acc;
						if (!acc.some((i) => i.Id === trans.Id)) {
							acc.push({
								Id: trans.Id,
								invoiceNumber: getPolicyTransactionInvoiceLabel(trans),
								amount: formatCurrency({
									num: trans.Balance,
									showDecimal: true,
								}),
								classOfRisk: trans.ClassOfRiskName,
							});
						}
						return acc;
					}, []);
				return (
					<DataGridDealPolicies
						{...{
							...row,
							transactions,
							policiesLoading: row.policiesLoading,
						}}
					/>
				);
			};
			break;
		case 'balance':
			column.renderCell = ({ row }) => {
				if (row.policiesLoading) {
					return <Skeleton variant='text' width='4em' />;
				}
				const balance = (row?.policies ?? [])
					.map((p) => p?.transaction)
					.filter(Boolean)
					.reduce((acc, trans) => {
						if (!trans?.Id || trans.Balance == null) return acc;
						return acc + trans.Balance;
					}, 0);
				return formatCurrency({ num: balance, showDecimal: true });
			};
			column.align = 'right';
			break;
		case 'subject':
			column.renderCell = ({ row }) => {
				return <DataGridTaskName row={row} />;
			};
			break;

		case 'taskType':
			column.renderCell = ({ row }) => {
				return <DataGridTaskType taskType={row.taskType} />;
			};
			break;

		case 'fullName':
			column.valueGetter = (_, row) => row.fullName || '-';
			break;
		case 'status': {
			if (objectType === 'tasks') {
				column.renderCell = ({ row }) => {
					return <DataGridTaskStatus row={row} />;
				};
				column.minWidth = 50;
				column.maxWidth = 50;
				column.width = 50;
			}
			break;
		}
		case 'relationshipStatus': {
			if (objectType === 'relationshipContact') {
				column.valueGetter = (_, row) =>
					RELATIONSHIP_CONTACT_STATUS_LABELS[row.relationshipStatus] ??
					row.relationshipStatus ??
					'-';
			}
			break;
		}
		case 'relationshipType': {
			if (objectType === 'relationshipContact') {
				column.valueGetter = (_, row) =>
					RELATIONSHIP_CONTACT_TYPE_LABELS[row.relationshipType] ??
					row.relationshipType ??
					'-';
			}
			break;
		}
		case 'referralCount': {
			if (objectType === 'relationshipContact') {
				column.sortable = false;
				column.renderCell = ({ row }) => {
					if (row.relationshipType !== 'referrer' || !row.email) {
						return <Typography>-</Typography>;
					}
					if (row.referralCountLoading) {
						return <Typography color='text.secondary'>…</Typography>;
					}
					return <Typography>{row.referralCount ?? 0}</Typography>;
				};
			}
			break;
		}
		case 'lastContactedAt':
		case 'nextActionDate':
			column.renderCell = ({ row }) => {
				return <DataGridFormattedDate date={row[field]} />;
			};
			break;
		case 'category':
			column.renderCell = ({ row }) => {
				return <DataGridTaskCategory categoryId={row.category} />;
			};
			break;

		case 'priority':
			column.renderCell = ({ row }) => {
				return row.priority ? <PriorityChip priority={row.priority} /> : '-';
			};
			break;

		case 'objectType':
			column.renderCell = ({ row }) => {
				return <DataGridTaskObjectType row={row} />;
			};
			break;
		case 'yearFolder': {
			column.width = 70;
			column.minWidth = 70;
			column.maxWidth = 70;
			break;
		}
		default:
			break;
	}

	return column;
};

// Doc-id sibling of each Hubspot-era owner field. `createdBy`/`lastUpdatedBy`
// have no sibling — they hold the actor's doc id themselves.
const OWNER_DOC_ID_FIELDS = {
	ownerId: 'ownerDocId',
	clientOwnerId: 'clientOwnerDocId',
};

const getOwnerDocId = (row, field) => row?.[OWNER_DOC_ID_FIELDS[field] ?? field];

export const parseValueForCsv = ({
	column,
	brokerGroupUsers,
	brokerGroups,
	pipelines,
	referralFields,
	stages,
	value,
}) => {
	const field = column.field;
	switch (field) {
		case 'clientOwnerDocId':
		case 'ownerDocId':
		case 'clientOwnerId':
		case 'ownerId':
		case 'createdBy':
		case 'lastUpdatedBy': {
			const owner = brokerGroupUsers.find((user) => user.id === getOwnerDocId(value, field));
			return owner?.name ?? '';
		}
		case 'brokerGroupId': {
			const brokerGroup = brokerGroups.find((b) => String(b.id) === String(value[field]));
			return brokerGroup?.name ?? '';
		}
		case 'isProspect': {
			return value[field] ? 'Prospect' : 'Client';
		}
		case 'automationEnabled': {
			return value[field] ? 'On' : 'Off';
		}
		case 'createdDate':
		case 'dueDate':
		case 'dateCompleted':
		case 'createDate':
		case 'createdWhen':
		case 'dateCreated':
		case 'estimatedCloseDate':
		case 'renewalDate':
		case 'dateOfLoss':
		case 'dateClosed':
		case 'nextTaskDate':
		case 'lastUpdated':
		case 'timestamp': {
			const includeTime = ['lastUpdated', 'nextTaskDate', 'dueDate', 'timestamp'].includes(
				field,
			);
			return value[field] ? formatDate(safelyParseDate(value[field]), includeTime) : '';
		}
		case 'actionType':
		case 'fieldChanged':
			return value[field] ? formatKey(value[field]) : '';
		case 'performedBy':
		case 'targetUser': {
			const user = value[field];
			if (typeof user === 'string') return user;
			const owner = brokerGroupUsers.find((brokerUser) => brokerUser.id === user?.userId);
			return owner?.name ?? user?.name ?? user?.email ?? '';
		}
		case 'dealStage': {
			const isArchived = value?.status === DEAL_STATUS_TYPES.archived;
			const currentStage =
				stages?.find((s) => String(s.stageId) === String(value[field]))?.label ??
				value[field];
			const stageLabel = isArchived ? 'Archived' : (currentStage ?? 'Unknown');
			return stageLabel;
		}
		case 'stage': {
			const storedStage = value[field];
			return (
				stages?.find((s) => s.stageId === storedStage || s.id === storedStage)?.label ??
				storedStage ??
				''
			);
		}
		case 'effectiveDate': {
			const effectiveDate = value[field];
			const currentStage = stages?.find((s) => s.stageId === value?.dealStage);

			if (!effectiveDate || currentStage?.order !== 0) return '-';
			const days = getOutstandingDays(effectiveDate);
			if (days === 1) return '1 day';
			return `${days} days`;
		}
		case 'prospectAmount':
		case 'dealAmount': {
			const num = value[field] ?? 0;
			return safelyParseFloat(num);
		}
		case 'policies': {
			const policies = value[field] ?? [];
			return policies
				.filter(Boolean)
				.map(
					(p) =>
						`${p.classOfRisk ?? p.ClassOfRisk} - ${p.policyNumber ?? p.PolicyNumber}`,
				)
				.join('; ');
		}
		case 'invoices': {
			const transactions = (value.policies ?? [])
				.map((p) => p?.transaction)
				.filter(Boolean)
				.reduce((acc, trans) => {
					if (!trans?.Id) return acc;
					if (!acc.some((i) => i.Id === trans.Id)) {
						acc.push({
							Id: trans.Id,
							invoiceNumber: getPolicyTransactionInvoiceLabel(trans),
							amount: formatCurrency({
								num: trans.Balance,
								showDecimal: true,
							}),
							classOfRisk: trans.ClassOfRiskName,
						});
					}
					return acc;
				}, []);

			return transactions
				.map((p) => `${p.invoiceNumber} | ${p.classOfRisk} | ${p.amount}`)
				.join('; ');
		}
		case 'balance': {
			const balance = (value?.policies ?? [])
				.map((p) => p?.transaction)
				.filter(Boolean)
				.reduce((acc, trans) => {
					if (!trans?.Id || trans.Balance == null) return acc;
					return acc + trans.Balance;
				}, 0);
			return formatCurrency({ num: balance, showDecimal: true });
		}
		case 'relationshipStatus':
			return (
				RELATIONSHIP_CONTACT_STATUS_LABELS[value.relationshipStatus] ??
				value.relationshipStatus ??
				''
			);
		case 'status':
			return value.completed ? 'Completed' : 'Not Started';
		case 'lifeInsuranceReferralStatus':
		case 'kiwiSaverReferralStatus': {
			const configField = referralFields?.find((f) => f.name === field);
			const label = (configField?.field?.options ?? []).find(
				(o) => o.value === value[field],
			)?.label;
			return label ?? value[field] ?? '';
		}
		case 'deal':
			return value.clientName ?? '';
		case 'contactName':
			return value.contactName?.toString?.().trim() || '';
		case 'informedConsentStatus':
			return (
				CONSENT_STATUS_OPTIONS.find((option) => option.value === getRowConsentStatus(value))
					?.label ?? ''
			);
		default:
			return value[field] ?? '';
	}
};

export const getSearchRecordPath = (row) => {
	if (!row) return null;
	if (row.indexName === 'tasks') {
		if (row.dealId) {
			return `${paths.deals}/${row.dealId}/tasks`;
		}
		if (row.clientId) {
			return `${paths.clients}/${row.clientId}/tasks`;
		}
		if (row.relationshipCompanyId) {
			return `${paths.companies}/${row.relationshipCompanyId}/tasks`;
		}
		return `${paths.tasks}/in-progress`;
	}
	if (!row.id) return null;
	if (row.indexName === 'clients') {
		return `${row?.isProspect ? paths.prospects : paths.clients}/${row.id}`;
	}
	if (row.indexName === 'deals') {
		const pathname = getUrlFromPipeline(row.pipeline);
		return `${pathname ?? paths.deals}/${row.id}`;
	}
	if (row.indexName === 'relationshipCompanies') {
		return `${paths.companies}/${row.id}`;
	}
	return `${row.id}`;
};

export const getSearchRecordNavigateOptions = (row) => {
	if (row?.indexName === 'tasks') {
		return { state: { task: row.id } };
	}
	return undefined;
};
